/*
 * RecognitionEditorPage: Erkennungssets bearbeiten (docs/anlagenerkennung.md).
 *
 * Ein Set besteht aus Prüfpunkten (Variablen, deren Wert die Ausprägung der
 * Anlage verrät, etwa Konfiguration/Heizkreis 1), Regeln (Ausdruck auf einen
 * Prüfpunkt, etwa "= Ein" oder "= gemischt") und je Regel einem Baustein aus
 * Datenpunkten. Bedienung: links Set und Fassung, in der Mitte der ganze Baum
 * einer Quelle, rechts Prüfpunkte und Regeln. Eine Regel aktiv schalten, dann
 * im Baum ihre Datenpunkte anhaken. Speichern legt immer eine neue Fassung an.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { showToast } from '../components/Toaster';
import './RecognitionEditorPage.css';

// ------------------------------------------------------------------ Typen

interface SnapNode {
  clientId: string; parentClientId: string | null; symbol: string; displayName: string | null;
  origin: number; category: string | null; typicalDefinitionId: string | null; typicalDefinitionVersion: number | null;
  overridesJson: string | null; attributesJson: string | null; sortOrder: number;
}
interface SnapLeaf {
  parentNodeClientId: string; symbol: string; displayName: string | null; valueType: number;
  address: string | null; enumEntriesJson: string | null; unit: string | null; writability: number;
  [k: string]: unknown;
}
interface Probe {
  key: string; symbol: string; address: string; displayName: string; valueType: number; enumEntriesJson: string | null;
}
interface Condition {
  probe?: string | null; feature?: string | null; eq?: unknown; ne?: unknown; in?: unknown[] | null;
  gt?: number | null; lt?: number | null; any?: Condition[] | null; all?: Condition[] | null;
}
interface Feature { key: string; label: string; cases: { when: Condition | null; value: string; text: string | null }[] }
interface Module { key: string; label: string; when: Condition | null; leaves: SnapLeaf[] }
interface Profile {
  id: string; version: number; name: string; description: string | null; protocols: string[];
  probes: Probe[]; features: Feature[];
  device: { symbol: string; name: string; protocol: string | null; commandCapable: boolean; forceCapable: boolean };
  nodes: SnapNode[]; modules: Module[];
}
interface VersionRow {
  key: string; version: number; name: string; protocols: string[]; builtIn: boolean; note: string | null;
  createdBy: string | null; createdAt: string | null; probes: number; modules: number; leaves: number;
}
interface SourceRef { kind: string; id: string; label: string; protocol: string; points: number }
interface Source { kind: string; id: string; label: string; protocol: string; nodes: SnapNode[]; leaves: SnapLeaf[] }

type Op = 'eq' | 'ne' | 'in' | 'gt' | 'lt';
interface SimpleCond { subject: string; op: Op; value: string }

const OPS: { op: Op; label: string }[] = [
  { op: 'eq', label: '=' }, { op: 'ne', label: '≠' }, { op: 'in', label: 'ist einer von' },
  { op: 'gt', label: '>' }, { op: 'lt', label: '<' },
];

const leafKey = (l: SnapLeaf) => `${l.parentNodeClientId}/${l.symbol}`;

function emptyProfile(protocol: string): Profile {
  return {
    id: '', version: 0, name: 'Neues Set', description: null, protocols: [protocol || 'bsb'],
    probes: [], features: [],
    device: { symbol: 'regler', name: 'Regler', protocol: protocol || 'bsb', commandCapable: true, forceCapable: false },
    nodes: [], modules: [{ key: 'basis', label: 'Grundausstattung', when: null, leaves: [] }],
  };
}

function slug(text: string): string {
  return text.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'x';
}

/** Eine Bedingung in die einfache Form, wenn sie hineinpasst: eine Zeile oder
 *  mehrere mit "und". Sonst null, dann zeigt der Editor JSON. */
function toSimple(c: Condition | null): SimpleCond[] | null {
  if (!c) return [];
  const one = (x: Condition): SimpleCond | null => {
    if (x.any || x.all) return null;
    const subject = x.probe ? `probe:${x.probe}` : x.feature ? `feature:${x.feature}` : '';
    if (!subject) return null;
    if (x.eq !== undefined && x.eq !== null) return { subject, op: 'eq', value: String(x.eq) };
    if (x.ne !== undefined && x.ne !== null) return { subject, op: 'ne', value: String(x.ne) };
    if (x.in) return { subject, op: 'in', value: x.in.map(String).join(', ') };
    if (x.gt !== undefined && x.gt !== null) return { subject, op: 'gt', value: String(x.gt) };
    if (x.lt !== undefined && x.lt !== null) return { subject, op: 'lt', value: String(x.lt) };
    return null;
  };
  if (c.all) {
    const list = c.all.map(one);
    return list.every(Boolean) ? (list as SimpleCond[]) : null;
  }
  const single = one(c);
  return single ? [single] : null;
}

function fromSimple(list: SimpleCond[]): Condition | null {
  const conv = (s: SimpleCond): Condition => {
    const [kind, key] = s.subject.split(':');
    const base: Condition = kind === 'probe' ? { probe: key } : { feature: key };
    const v = (t: string): unknown => (t.trim() !== '' && !Number.isNaN(Number(t)) ? Number(t) : t.trim());
    if (s.op === 'in') return { ...base, in: s.value.split(',').map((x) => v(x)).filter((x) => x !== '') };
    if (s.op === 'gt' || s.op === 'lt') return { ...base, [s.op]: Number(s.value) };
    return { ...base, [s.op]: v(s.value) };
  };
  const valid = list.filter((s) => s.subject);
  if (valid.length === 0) return null;
  return valid.length === 1 ? conv(valid[0]) : { all: valid.map(conv) };
}

function enumOptions(json: string | null): { value: number; displayName: string }[] {
  if (!json) return [];
  try { return JSON.parse(json); } catch { return []; }
}

// ------------------------------------------------------------------ Seite

export function RecognitionEditorPage() {
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [key, setKey] = useState('');
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null);
  const [sources, setSources] = useState<SourceRef[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [source, setSource] = useState<Source | null>(null);
  const [active, setActive] = useState<string>('basis');
  const [filter, setFilter] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reloadVersions = () => api.get<VersionRow[]>('/api/recognition/profiles').then(setVersions).catch(() => setVersions([]));
  useEffect(() => {
    reloadVersions();
    api.get<SourceRef[]>('/api/recognition/sources').then(setSources).catch(() => setSources([]));
  }, []);

  const loadProfile = async (k: string, v: number) => {
    if (dirty && !confirm('Ungespeicherte Änderungen verwerfen?')) return;
    const p = await api.get<Profile>(`/api/recognition/profiles/${encodeURIComponent(k)}?version=${v}`);
    setProfile(p);
    setKey(p.id);
    setLoadedFrom(`${p.id} · Fassung ${p.version}`);
    setActive(p.modules[0]?.key ?? 'basis');
    setDirty(false);
    // Passende Quelle vorschlagen: der erste Katalog des Protokolls.
    if (!sourceId) {
      const s = sources.find((x) => x.protocol === (p.protocols[0] ?? '') && x.kind === 'catalog');
      if (s) pickSource(`${s.kind}:${s.id}`);
    }
  };

  const pickSource = async (value: string) => {
    setSourceId(value);
    setSource(null);
    if (!value) return;
    const [kind, ...rest] = value.split(':');
    try {
      setSource(await api.get<Source>(`/api/recognition/sources/${kind}/${rest.join(':')}`));
    } catch (e) {
      showToast({ tone: 'error', title: 'Quelle nicht geladen', message: e instanceof Error ? e.message : String(e) });
    }
  };

  const update = (fn: (p: Profile) => Profile) => {
    setProfile((p) => (p ? fn(structuredClone(p)) : p));
    setDirty(true);
  };

  // Alle Datenpunkte: aus der Quelle, dazu was im Set steht und in der Quelle fehlt.
  const tree = useMemo(() => {
    const nodes = new Map<string, { node: SnapNode; leaves: SnapLeaf[] }>();
    const add = (n: SnapNode | undefined, l: SnapLeaf) => {
      const id = l.parentNodeClientId;
      if (!nodes.has(id)) {
        nodes.set(id, {
          node: n ?? { clientId: id, parentClientId: null, symbol: id.replace(/^n-/, ''), displayName: id.replace(/^n-/, ''),
            origin: 0, category: null, typicalDefinitionId: null, typicalDefinitionVersion: null, overridesJson: null,
            attributesJson: null, sortOrder: 0 },
          leaves: [],
        });
      }
      const entry = nodes.get(id)!;
      if (!entry.leaves.some((x) => x.symbol === l.symbol)) entry.leaves.push(l);
    };
    for (const l of source?.leaves ?? []) add(source!.nodes.find((n) => n.clientId === l.parentNodeClientId), l);
    for (const m of profile?.modules ?? []) {
      for (const l of m.leaves) add(profile!.nodes.find((n) => n.clientId === l.parentNodeClientId), l);
    }
    return [...nodes.values()].sort((a, b) => (a.node.displayName ?? '').localeCompare(b.node.displayName ?? '', 'de'));
  }, [source, profile]);

  const activeModule = profile?.modules.find((m) => m.key === active) ?? null;
  const activeKeys = new Set(activeModule?.leaves.map(leafKey) ?? []);
  const usedBy = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of profile?.modules ?? []) for (const l of m.leaves) {
      const k = leafKey(l);
      map.set(k, [...(map.get(k) ?? []), m.label]);
    }
    return map;
  }, [profile]);
  const probeAddresses = new Set(profile?.probes.map((p) => p.address) ?? []);

  const toggleLeaf = (l: SnapLeaf, node: SnapNode) => update((p) => {
    const m = p.modules.find((x) => x.key === active);
    if (!m) return p;
    const k = leafKey(l);
    if (m.leaves.some((x) => leafKey(x) === k)) m.leaves = m.leaves.filter((x) => leafKey(x) !== k);
    else {
      m.leaves.push(structuredClone(l));
      if (!p.nodes.some((n) => n.clientId === node.clientId)) p.nodes.push(structuredClone(node));
    }
    return p;
  });

  const toggleNode = (entry: { node: SnapNode; leaves: SnapLeaf[] }, on: boolean) => update((p) => {
    const m = p.modules.find((x) => x.key === active);
    if (!m) return p;
    const keys = new Set(entry.leaves.map(leafKey));
    m.leaves = m.leaves.filter((x) => !keys.has(leafKey(x)));
    if (on) {
      m.leaves.push(...entry.leaves.map((l) => structuredClone(l)));
      if (!p.nodes.some((n) => n.clientId === entry.node.clientId)) p.nodes.push(structuredClone(entry.node));
    }
    return p;
  });

  const markProbe = (l: SnapLeaf) => update((p) => {
    if (p.probes.some((x) => x.address === l.address)) {
      p.probes = p.probes.filter((x) => x.address !== l.address);
      return p;
    }
    let k = slug(l.displayName ?? l.symbol);
    for (let n = 2; p.probes.some((x) => x.key === k); n++) k = `${slug(l.displayName ?? l.symbol)}_${n}`;
    p.probes.push({
      key: k, symbol: l.symbol, address: l.address ?? '', displayName: l.displayName ?? l.symbol,
      valueType: l.valueType, enumEntriesJson: l.enumEntriesJson,
    });
    return p;
  });

  const addRule = (probe?: Probe) => update((p) => {
    let k = probe ? `${probe.key}_regel` : 'regel';
    for (let n = 2; p.modules.some((m) => m.key === k); n++) k = `${probe ? probe.key : 'regel'}_${n}`;
    const first = probe ? enumOptions(probe.enumEntriesJson)[1] ?? enumOptions(probe.enumEntriesJson)[0] : undefined;
    p.modules.push({
      key: k,
      label: probe ? `${probe.displayName} = ${first?.displayName ?? '…'}` : 'Neue Regel',
      when: probe ? { probe: probe.key, eq: first?.value ?? 1 } : null,
      leaves: [],
    });
    setActive(k);
    return p;
  });

  const save = async () => {
    if (!profile) return;
    if (!key.trim()) { showToast({ tone: 'error', title: 'Schlüssel fehlt', message: 'Bitte einen Schlüssel für das Set vergeben.' }); return; }
    setBusy(true);
    try {
      const saved = await api.post<Profile>(`/api/recognition/profiles/${encodeURIComponent(key.trim())}`, { profile, note });
      setProfile(saved);
      setKey(saved.id);
      setLoadedFrom(`${saved.id} · Fassung ${saved.version}`);
      setDirty(false);
      setNote('');
      reloadVersions();
      showToast({ tone: 'success', title: `Fassung ${saved.version} gespeichert`, message: saved.name });
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gespeichert',
        message: e instanceof ApiError ? (e.body as { error?: string })?.error ?? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const keys = [...new Set(versions.map((v) => v.key))];
  const q = filter.trim().toLowerCase();

  return (
    <div class="hv-recog-ed">
      <header class="hv-recog-ed__head">
        <h1>Erkennungssets</h1>
        <p class="muted">
          Prüfpunkte markieren, Regeln mit Ausdruck anlegen, je Regel die Datenpunkte anhaken.
          Jedes Speichern ergibt eine neue Fassung; beim Erkennen einer Anlage wird gewählt, welche gilt.
        </p>
      </header>

      <div class="hv-recog-ed__cols">
        {/* ---------------------------------------------- Sets und Fassungen */}
        <aside class="hv-recog-ed__sets">
          <button type="button" class="hv-btn hv-btn--sm" onClick={() => {
            if (dirty && !confirm('Ungespeicherte Änderungen verwerfen?')) return;
            setProfile(emptyProfile(sources.find((s) => s.id === sourceId.split(':')[1])?.protocol ?? 'bsb'));
            setKey(''); setLoadedFrom(null); setActive('basis'); setDirty(true);
          }}>+ Neues Set</button>
          {keys.map((k) => (
            <div key={k} class="hv-recog-ed__set">
              <div class="hv-recog-ed__set-name">{versions.filter((v) => v.key === k).at(-1)?.name}</div>
              <div class="hv-recog-ed__versions">
                {versions.filter((v) => v.key === k).map((v) => (
                  <button key={v.version} type="button"
                          class={`hv-recog-ed__ver${profile?.id === k && profile.version === v.version ? ' is-active' : ''}`}
                          title={`${v.builtIn ? 'mitgeliefert' : `${v.createdBy ?? ''} ${v.createdAt ? new Date(v.createdAt).toLocaleString('de-AT') : ''}`}${v.note ? ` · ${v.note}` : ''} · ${v.probes} Prüfpunkte, ${v.modules} Regeln, ${v.leaves} Punkte`}
                          onClick={() => loadProfile(k, v.version)}>
                    v{v.version}{v.builtIn ? '*' : ''}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <p class="hv-modal__hint">* mitgeliefert, nur als Vorlage für eine neue Fassung.</p>
        </aside>

        {!profile && <div class="hv-recog-ed__empty muted">Links ein Set und eine Fassung wählen oder ein neues anlegen.</div>}

        {profile && (
          <>
            {/* -------------------------------------------------- Baum */}
            <section class="hv-recog-ed__tree">
              <div class="hv-recog-ed__toolbar">
                <select value={sourceId} onChange={(e) => pickSource((e.target as HTMLSelectElement).value)}>
                  <option value="">Quelle wählen…</option>
                  {sources.map((s) => (
                    <option key={`${s.kind}:${s.id}`} value={`${s.kind}:${s.id}`}>{s.label}</option>
                  ))}
                </select>
                <input type="search" placeholder="Suchen (Name, Zeile, Adresse)" value={filter}
                       onInput={(e) => setFilter((e.target as HTMLInputElement).value)} />
                <label class="hv-recog-ed__check">
                  <input type="checkbox" checked={onlySelected} onChange={(e) => setOnlySelected((e.target as HTMLInputElement).checked)} />
                  nur im Set
                </label>
              </div>
              <div class="hv-recog-ed__mode">
                Bearbeite: <b>{activeModule?.label ?? '–'}</b>
                {activeModule && <span class="muted"> · {activeModule.leaves.length} Punkte angehakt</span>}
              </div>
              <div class="hv-recog-ed__list">
                {tree.map((entry) => {
                  const leaves = entry.leaves.filter((l) =>
                    (!q || `${l.displayName} ${l.symbol} ${l.address}`.toLowerCase().includes(q))
                    && (!onlySelected || usedBy.has(leafKey(l)) || probeAddresses.has(l.address ?? '')));
                  if (leaves.length === 0) return null;
                  const isOpen = open[entry.node.clientId] ?? (!!q || onlySelected);
                  const checked = leaves.filter((l) => activeKeys.has(leafKey(l))).length;
                  return (
                    <div key={entry.node.clientId} class="hv-recog-ed__node">
                      <div class="hv-recog-ed__node-head">
                        <button type="button" class="hv-recog-ed__caret"
                                onClick={() => setOpen({ ...open, [entry.node.clientId]: !isOpen })}>{isOpen ? '▾' : '▸'}</button>
                        <input type="checkbox" checked={checked === leaves.length}
                               ref={(el) => { if (el) el.indeterminate = checked > 0 && checked < leaves.length; }}
                               onChange={(e) => toggleNode({ node: entry.node, leaves }, (e.target as HTMLInputElement).checked)} />
                        <span class="hv-recog-ed__node-name">{entry.node.displayName ?? entry.node.symbol}</span>
                        <span class="muted">{checked > 0 ? `${checked} / ` : ''}{leaves.length}</span>
                      </div>
                      {isOpen && leaves.map((l) => {
                        const k = leafKey(l);
                        const others = (usedBy.get(k) ?? []).filter((x) => x !== activeModule?.label);
                        const isProbe = probeAddresses.has(l.address ?? '');
                        return (
                          <div key={k} class={`hv-recog-ed__leaf${activeKeys.has(k) ? ' is-on' : ''}`}>
                            <input type="checkbox" checked={activeKeys.has(k)} onChange={() => toggleLeaf(l, entry.node)} />
                            <span class="hv-recog-ed__leaf-name">{l.displayName ?? l.symbol}</span>
                            <span class="mono muted">{l.symbol}</span>
                            {others.length > 0 && <span class="hv-recog-ed__tag" title={others.join(', ')}>{others.length} Regel{others.length > 1 ? 'n' : ''}</span>}
                            <button type="button" class={`hv-recog-ed__probe-btn${isProbe ? ' is-on' : ''}`}
                                    title={isProbe ? 'Prüfpunkt entfernen' : 'Als Prüfpunkt markieren'}
                                    onClick={() => markProbe(l)}>◎</button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {!source && <p class="muted">Eine Quelle wählen, um den ganzen Baum zu sehen. Ohne Quelle zeigt der Baum nur, was im Set steht.</p>}
              </div>
            </section>

            {/* --------------------------------------- Prüfpunkte und Regeln */}
            <section class="hv-recog-ed__rules">
              <div class="hv-recog-ed__meta">
                <label class="hv-modal__field"><span>Name</span>
                  <input value={profile.name} onInput={(e) => update((p) => ({ ...p, name: (e.target as HTMLInputElement).value }))} /></label>
                <div class="hv-recog-ed__row">
                  <label class="hv-modal__field"><span>Schlüssel</span>
                    <input value={key} placeholder="z. b. carel-ecoforest" onInput={(e) => setKey((e.target as HTMLInputElement).value)} /></label>
                  <label class="hv-modal__field"><span>Protokoll</span>
                    <input value={profile.protocols.join(', ')}
                           onInput={(e) => update((p) => ({ ...p, protocols: (e.target as HTMLInputElement).value.split(',').map((x) => x.trim()).filter(Boolean) }))} /></label>
                </div>
                {loadedFrom && <p class="hv-modal__hint">Geladen: {loadedFrom}{dirty ? ', geändert' : ''}</p>}
              </div>

              <h3>Prüfpunkte</h3>
              {profile.probes.length === 0 && <p class="muted">Im Baum mit ◎ markieren.</p>}
              <ul class="hv-recog-ed__probes">
                {profile.probes.map((p) => (
                  <li key={p.key}>
                    <span class="mono">{p.key}</span>
                    <span>{p.displayName}</span>
                    <button type="button" class="hv-btn hv-btn--sm" onClick={() => addRule(p)}>+ Regel</button>
                  </li>
                ))}
              </ul>

              <h3>Regeln <button type="button" class="hv-btn hv-btn--sm" onClick={() => addRule()}>+ Regel</button></h3>
              {profile.modules.map((m) => (
                <RuleCard key={m.key} profile={profile} module={m} active={active === m.key}
                          onActivate={() => setActive(m.key)}
                          onChange={(next) => update((p) => ({ ...p, modules: p.modules.map((x) => (x.key === m.key ? next : x)) }))}
                          onDelete={() => update((p) => ({ ...p, modules: p.modules.filter((x) => x.key !== m.key) }))} />
              ))}

              <div class="hv-recog-ed__save">
                <input placeholder="Notiz zur Fassung (optional)" value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
                <button type="button" class="hv-btn hv-btn--primary" disabled={busy || !dirty} onClick={save}>
                  {busy ? 'Speichere…' : 'Als neue Fassung speichern'}
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Regelkarte

interface RuleProps {
  profile: Profile; module: Module; active: boolean;
  onActivate(): void; onChange(m: Module): void; onDelete(): void;
}

function RuleCard({ profile, module: m, active, onActivate, onChange, onDelete }: RuleProps) {
  const simple = toSimple(m.when);
  const [json, setJson] = useState(JSON.stringify(m.when, null, 1));
  const subjects = [
    ...profile.probes.map((p) => ({ value: `probe:${p.key}`, label: p.displayName })),
    ...profile.features.map((f) => ({ value: `feature:${f.key}`, label: `Merkmal ${f.label}` })),
  ];
  const valueOptions = (subject: string): { value: string; label: string }[] => {
    const [kind, k] = subject.split(':');
    if (kind === 'probe') {
      return enumOptions(profile.probes.find((p) => p.key === k)?.enumEntriesJson ?? null)
        .map((o) => ({ value: String(o.value), label: o.displayName }));
    }
    const f = profile.features.find((x) => x.key === k);
    return [...new Map((f?.cases ?? []).map((c) => [c.value, { value: c.value, label: c.text ?? c.value }])).values()];
  };
  const setSimple = (list: SimpleCond[]) => onChange({ ...m, when: fromSimple(list) });

  return (
    <div class={`hv-recog-ed__rule${active ? ' is-active' : ''}`} onClick={onActivate}>
      <div class="hv-recog-ed__rule-head">
        <input type="radio" checked={active} onChange={onActivate} title="Diese Regel im Baum bearbeiten" />
        <input class="hv-recog-ed__rule-label" value={m.label}
               onInput={(e) => onChange({ ...m, label: (e.target as HTMLInputElement).value })} />
        <span class="muted">{m.leaves.length} Punkte</span>
        {m.key !== 'basis' && (
          <button type="button" class="hv-recog-ed__del" title="Regel löschen"
                  onClick={(e) => { e.stopPropagation(); if (confirm(`Regel "${m.label}" löschen?`)) onDelete(); }}>×</button>
        )}
      </div>
      {m.when === null && simple?.length === 0 && (
        <div class="hv-recog-ed__cond muted">
          immer
          {m.key !== 'basis' && (
            <button type="button" class="hv-btn hv-btn--sm" onClick={() => setSimple([{ subject: subjects[0]?.value ?? '', op: 'eq', value: '1' }])}>
              Bedingung
            </button>
          )}
        </div>
      )}
      {simple && simple.length > 0 && simple.map((c, i) => {
        const opts = valueOptions(c.subject);
        return (
          <div key={i} class="hv-recog-ed__cond">
            {i > 0 && <span class="muted">und</span>}
            <select value={c.subject} onChange={(e) => setSimple(simple.map((x, j) => (j === i ? { ...x, subject: (e.target as HTMLSelectElement).value } : x)))}>
              {subjects.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={c.op} onChange={(e) => setSimple(simple.map((x, j) => (j === i ? { ...x, op: (e.target as HTMLSelectElement).value as Op } : x)))}>
              {OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>
            {opts.length > 0 && c.op !== 'in' && c.op !== 'gt' && c.op !== 'lt' ? (
              <select value={c.value} onChange={(e) => setSimple(simple.map((x, j) => (j === i ? { ...x, value: (e.target as HTMLSelectElement).value } : x)))}>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input value={c.value} placeholder={c.op === 'in' ? '1, 3' : 'Wert'}
                     onInput={(e) => setSimple(simple.map((x, j) => (j === i ? { ...x, value: (e.target as HTMLInputElement).value } : x)))} />
            )}
            <button type="button" class="hv-recog-ed__del" title="Bedingung entfernen"
                    onClick={() => setSimple(simple.filter((_, j) => j !== i))}>×</button>
          </div>
        );
      })}
      {simple && simple.length > 0 && (
        <button type="button" class="hv-btn hv-btn--sm" onClick={() => setSimple([...simple, { subject: subjects[0]?.value ?? '', op: 'eq', value: '1' }])}>
          + und
        </button>
      )}
      {simple === null && (
        <div class="hv-recog-ed__cond">
          <textarea rows={3} value={json} onInput={(e) => setJson((e.target as HTMLTextAreaElement).value)}
                    onBlur={() => { try { onChange({ ...m, when: JSON.parse(json) }); } catch { /* bleibt, bis es gültig ist */ } }} />
          <span class="muted">Verschachtelte Regel (oder/und), als JSON.</span>
        </div>
      )}
    </div>
  );
}
